import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Card,
  Row,
  Col,
  message,
  Popconfirm,
  Tooltip,
  Badge,
  Select,
  InputNumber,
  DatePicker,
  Switch,
  Modal,
  Form,
  Alert,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  DollarOutlined,
  PercentageOutlined,
  CalendarOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import type {
  HRPayrollItem,
  HREmployeePayrollItem,
  HREmployee,
  EmployeePayrollItemFormData,
} from "../../types/hr";
import { useHRAPI } from "../../hooks/useHRAPI";

const { Title, Text } = Typography;
const { Option } = Select;

interface EmployeePayrollItemsProps {
  employee: HREmployee;
  visible: boolean;
  onClose: () => void;
}

const EmployeePayrollItems: React.FC<EmployeePayrollItemsProps> = ({
  employee,
  visible,
  onClose,
}) => {
  const [employeePayrollItems, setEmployeePayrollItems] = useState<
    HREmployeePayrollItem[]
  >([]);
  const [availablePayrollItems, setAvailablePayrollItems] = useState<
    HRPayrollItem[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<HREmployeePayrollItem | null>(
    null
  );
  const [form] = Form.useForm();

  const {
    getEmployeePayrollItems,
    getPayrollItems,
    assignPayrollItemToEmployee,
    updateEmployeePayrollItem,
    removePayrollItemFromEmployee,
  } = useHRAPI();

  useEffect(() => {
    if (visible && employee) {
      fetchData();
    }
  }, [visible, employee]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeeItems, allItems] = await Promise.all([
        getEmployeePayrollItems(employee.id),
        getPayrollItems(),
      ]);

      setEmployeePayrollItems(employeeItems || []);
      setAvailablePayrollItems(allItems || []);
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
      message.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
    });
    setFormVisible(true);
  };

  const handleEdit = (item: HREmployeePayrollItem) => {
    setEditingItem(item);
    form.setFieldsValue({
      payroll_item_id: item.payroll_item_id,
      custom_amount: item.custom_amount,
      custom_percentage: item.custom_percentage,
      start_date: item.start_date ? dayjs(item.start_date) : null,
      end_date: item.end_date ? dayjs(item.end_date) : null,
      is_active: item.is_active,
    });
    setFormVisible(true);
  };

  const handleDelete = async (item: HREmployeePayrollItem) => {
    try {
      await removePayrollItemFromEmployee(employee.id, item.id);
      message.success("Élément de paie supprimé avec succès");
      fetchData();
    } catch (error: any) {
      message.error(error.message || "Erreur lors de la suppression");
    }
  };

  const handleFormSubmit = async (
    values: EmployeePayrollItemFormData & { start_date?: any; end_date?: any }
  ) => {
    try {
      const formData = {
        ...values,
        start_date: values.start_date
          ? dayjs(values.start_date).format("YYYY-MM-DD")
          : undefined,
        end_date: values.end_date
          ? dayjs(values.end_date).format("YYYY-MM-DD")
          : undefined,
      };

      if (editingItem) {
        await updateEmployeePayrollItem(employee.id, editingItem.id, formData);
        message.success("Élément de paie modifié avec succès");
      } else {
        await assignPayrollItemToEmployee(employee.id, formData);
        message.success("Élément de paie assigné avec succès");
      }

      setFormVisible(false);
      fetchData();
    } catch (error: any) {
      message.error(error.message || "Erreur lors de la sauvegarde");
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "gain":
        return "green";
      case "deduction":
        return "red";
      case "info":
        return "blue";
      default:
        return "default";
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case "gain":
        return "Gain";
      case "deduction":
        return "Déduction";
      case "info":
        return "Information";
      default:
        return type;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "salary":
        return "purple";
      case "allowance":
        return "cyan";
      case "bonus":
        return "gold";
      case "deduction":
        return "red";
      case "tax":
        return "volcano";
      case "social_contribution":
        return "orange";
      default:
        return "default";
    }
  };

  const getCategoryText = (category: string) => {
    switch (category) {
      case "salary":
        return "Salaire";
      case "allowance":
        return "Indemnité";
      case "bonus":
        return "Prime";
      case "deduction":
        return "Déduction";
      case "tax":
        return "Impôt";
      case "social_contribution":
        return "Cotisation sociale";
      default:
        return category;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getAssignedPayrollItemIds = () => {
    return employeePayrollItems.map((item) => item.payroll_item_id);
  };

  const getAvailableItems = () => {
    const assignedIds = getAssignedPayrollItemIds();
    return availablePayrollItems.filter(
      (item) => item.is_active && !assignedIds.includes(item.id)
    );
  };

  const selectedPayrollItem = Form.useWatch("payroll_item_id", form);
  const selectedItem = availablePayrollItems.find(
    (item) => item.id === selectedPayrollItem
  );

  const columns: ColumnsType<HREmployeePayrollItem> = [
    {
      title: "Élément de paie",
      key: "payroll_item",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            <Text code>{record.code}</Text> - {record.name}
          </div>
          {record.custom_description && (
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.custom_description}
            </Text>
          )}
          <div style={{ marginTop: 4 }}>
            <Tag
              color={getTypeColor(record.type || "")}
              style={{ fontSize: "11px" }}
            >
              {getTypeText(record.type || "")}
            </Tag>
            <Tag
              color={getCategoryColor(record.category || "")}
              style={{ fontSize: "11px" }}
            >
              {getCategoryText(record.category || "")}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: "Valeur personnalisée",
      key: "custom_value",
      width: 150,
      render: (_, record) => (
        <div>
          {record.custom_amount !== null &&
          record.custom_amount !== undefined ? (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <DollarOutlined />
              <Text strong>{formatCurrency(record.custom_amount)}</Text>
            </div>
          ) : record.custom_percentage !== null &&
            record.custom_percentage !== undefined ? (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <PercentageOutlined />
              <Text strong>{record.custom_percentage}%</Text>
            </div>
          ) : (
            <Text type="secondary">Valeur par défaut</Text>
          )}
        </div>
      ),
    },
    {
      title: "Période",
      key: "period",
      width: 150,
      render: (_, record) => (
        <div>
          {record.start_date && (
            <div style={{ fontSize: "12px" }}>
              <CalendarOutlined /> Début:{" "}
              {dayjs(record.start_date).format("DD/MM/YYYY")}
            </div>
          )}
          {record.end_date && (
            <div style={{ fontSize: "12px" }}>
              <CalendarOutlined /> Fin:{" "}
              {dayjs(record.end_date).format("DD/MM/YYYY")}
            </div>
          )}
          {!record.start_date && !record.end_date && (
            <Tooltip title="Affectation sans limites de dates renseignées. La date de saisie ne précise pas la date d'entrée en vigueur et ne conserve pas les anciens montants.">
              <Text type="secondary">Permanent · dates d’effet non précisées</Text>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Modifier">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Popconfirm
              title="Êtes-vous sûr de vouloir supprimer cette assignation ?"
              onConfirm={() => handleDelete(record)}
              okText="Oui"
              cancelText="Non"
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const stats = {
    total: employeePayrollItems.length,
    gains: employeePayrollItems.filter((item) => item.type === "gain").length,
    deductions: employeePayrollItems.filter((item) => item.type === "deduction")
      .length,
    active: employeePayrollItems.filter((item) => item.is_active).length,
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <UserOutlined />
          Éléments de paie - {employee.first_name} {employee.last_name}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
      destroyOnClose
    >
      <div style={{ marginBottom: "16px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text type="secondary">
            Gérez les éléments de paie assignés à cet employé
          </Text>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            disabled={getAvailableItems().length === 0}
          >
            Assigner un élément
          </Button>
        </div>
      </div>

      {/* Statistiques */}
      <Row gutter={16} style={{ marginBottom: "16px" }}>
        <Col span={6}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
                {stats.total}
              </Title>
              <Text type="secondary">Total</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <Title level={4} style={{ margin: 0, color: "#52c41a" }}>
                {stats.gains}
              </Title>
              <Text type="secondary">Gains</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <Title level={4} style={{ margin: 0, color: "#ff4d4f" }}>
                {stats.deductions}
              </Title>
              <Text type="secondary">Déductions</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <Title level={4} style={{ margin: 0, color: "#faad14" }}>
                {stats.active}
              </Title>
              <Text type="secondary">Actifs</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Tableau */}
      <Table
        columns={columns}
        dataSource={employeePayrollItems}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ y: 400 }}
        locale={{
          emptyText: "Aucun élément de paie assigné",
        }}
      />

      {/* Formulaire d'assignation */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SettingOutlined />
            {editingItem
              ? "Modifier l'assignation"
              : "Assigner un élément de paie"}
          </div>
        }
        open={formVisible}
        onCancel={() => setFormVisible(false)}
        onOk={() => form.submit()}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={{
            is_active: true,
          }}
        >
          {!editingItem && (
            <>
              <Form.Item
                label="Élément de paie"
                name="payroll_item_id"
                rules={[
                  {
                    required: true,
                    message: "Veuillez sélectionner un élément de paie",
                  },
                ]}
              >
                <Select
                  placeholder="Sélectionner un élément de paie"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {getAvailableItems().map((item) => (
                    <Option key={item.id} value={item.id}>
                      <div>
                        <Text code>{item.code}</Text> - {item.name}
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          <Tag
                            color={getTypeColor(item.type)}
                            style={{ fontSize: "11px" }}
                          >
                            {getTypeText(item.type)}
                          </Tag>
                          <Tag
                            color={getCategoryColor(item.category)}
                            style={{ fontSize: "11px" }}
                          >
                            {getCategoryText(item.category)}
                          </Tag>
                        </div>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {selectedItem && (
                <Alert
                  message={`Élément sélectionné: ${selectedItem.name}`}
                  description={
                    <div>
                      <p>
                        <strong>Type:</strong> {getTypeText(selectedItem.type)}
                      </p>
                      <p>
                        <strong>Catégorie:</strong>{" "}
                        {getCategoryText(selectedItem.category)}
                      </p>
                      <p>
                        <strong>Calcul:</strong> {selectedItem.calculation_type}
                      </p>
                      {selectedItem.custom_description && (
                        <p>
                          <strong>Description:</strong>{" "}
                          {selectedItem.custom_description}
                        </p>
                      )}
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
            </>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Montant personnalisé (FCFA)"
                name="custom_amount"
                help="Laissez vide pour utiliser la valeur par défaut"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Pourcentage personnalisé (%)"
                name="custom_percentage"
                help="Laissez vide pour utiliser la valeur par défaut"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  step={0.1}
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Date de début"
                name="start_date"
                help="Date d’entrée en vigueur du montant, qui peut précéder sa saisie. Sans date, son application aux périodes passées peut nécessiter une confirmation."
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Sélectionner une date"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Date de fin"
                name="end_date"
                help="Optionnel - Date de fin d'application"
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Sélectionner une date"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Élément actif"
            name="is_active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};

export default EmployeePayrollItems;
